import {useEffect, useState, useMemo, useCallback} from 'react';
import CollapaseIcon from '$assets/svgs/collapse-icon.svg?react';
import CloudDownloadOutlinedIcon from '$assets/svgs/download-cloud.svg?react';
import {Box, Typography, IconButton, CircularProgress} from '@mui/material';
import CoinsIcon from '$assets/svgs/coins-icon.svg?react';
import {
  expandedMonthlyRevenueBox,
  titleRow,
  titleText,
  headerButtons,
  mainValueRow,
  revenueAmount,
  leftSection,
  statsAndArrowsRow,
  arrowNavContainer,
  monthSelectorContainer,
  downloadButton,
  downloadButtonDisabled,
  circularProgress,
} from './styles';
import {
  getMonthlyRevenueData,
  MonthlyRevenueApiItem,
  MonthlyRevenueApiResponse,
} from '$api/finance/dashboard';
import {monthNames, windowSize} from '../../../constant';
import {isFutureMonth} from '~/utils/helper';
import ExpandedMonthlyRevenueChart from './ExpandedMonthlyRevenueChart';
import ExpandedMonthlyRevenueStats from './ExpandedMonthlyRevenueStats';
import ExpandedMonthlyRevenueMonthSelector from './ExpandedMonthlyRevenueMonthSelector';
import ArrowMonthNavigation from './ArrowMonthNavigation';
import ExpandedMonthlyRevenueSkeleton from './ExpandedMonthlyRevenueSkeleton';
import {useDownloadMonthlyRevenue} from '$modules/frontdesk/Finance/Dashboard/hooks';

function getCenteredWindowStart(idx: number, total: number) {
  let start = Math.max(0, idx - Math.floor(windowSize / 2));
  if (start + windowSize > total) {
    start = Math.max(0, total - windowSize);
  }
  return start;
}

interface ExpandedMonthlyRevenueProps {
  onClose: () => void;
}

const ExpandedMonthlyRevenue: React.FC<ExpandedMonthlyRevenueProps> = ({
  onClose,
}) => {
  const [monthlyData, setMonthlyData] = useState<MonthlyRevenueApiItem[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [windowStart, setWindowStart] = useState(0);
  const [yAxisTicks, setYAxisTicks] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const {isDownloading, handleDownload} = useDownloadMonthlyRevenue();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res: MonthlyRevenueApiResponse = await getMonthlyRevenueData();
        if (res?.success && res?.data?.chart_data?.length > 0) {
          setMonthlyData(res.data.chart_data);
          setYAxisTicks(res.data.y_axis_ticks || []);

          const now = new Date();
          const currentMonthName = `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
          const selectedIdx =
            res.data.chart_data.findIndex(d => d?.month === currentMonthName) ??
            0;
          setSelectedMonth(
            res.data.chart_data[selectedIdx]?.month ||
              res.data.chart_data[0]?.month
          );
          setWindowStart(
            getCenteredWindowStart(selectedIdx, res.data.chart_data.length)
          );
        } else {
          setError('No data available');
        }
      } catch (err) {
        setError('Failed to fetch data');
        console.error('Error fetching monthly revenue data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!monthlyData?.length || !selectedMonth) return;

    const idx = monthlyData.findIndex(d => d?.month === selectedMonth);
    if (idx !== -1) {
      setWindowStart(getCenteredWindowStart(idx, monthlyData.length));
    }
  }, [selectedMonth, monthlyData]);

  const months = useMemo(
    () => monthlyData?.map(d => d?.month).filter(Boolean) || [],
    [monthlyData]
  );

  const selectedData = useMemo(
    () => monthlyData?.find(d => d?.month === selectedMonth),
    [monthlyData, selectedMonth]
  );

  const chartData = useMemo(
    () =>
      monthlyData?.slice(windowStart, windowStart + windowSize).map(d => ({
        month: d?.month || '',
        value: d?.revenue || 0,
        bookingRate: d?.booking_rate || 0,
        estimatedRevenue: d?.estimated_revenue || 0,
      })) || [],
    [monthlyData, windowStart]
  );

  const bookingRate = selectedData?.booking_rate || 0;
  const revenue = selectedData?.revenue || 0;
  const estimatedRevenue = selectedData?.estimated_revenue || 0;
  const isFuture = selectedMonth ? isFutureMonth(selectedMonth) : false;

  const handlePrev = useCallback(
    () => setWindowStart(ws => Math.max(0, ws - 1)),
    []
  );

  const handleNext = useCallback(
    () =>
      setWindowStart(ws =>
        Math.min((monthlyData?.length || 0) - windowSize, ws + 1)
      ),
    [monthlyData?.length]
  );

  const onDownloadClick = useCallback(() => {
    if (selectedMonth) {
      handleDownload(selectedMonth);
    }
  }, [handleDownload, selectedMonth]);

  if (loading) {
    return <ExpandedMonthlyRevenueSkeleton />;
  }

  if (error) {
    return (
      <Box sx={expandedMonthlyRevenueBox}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!monthlyData?.length) {
    return (
      <Box sx={expandedMonthlyRevenueBox}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={expandedMonthlyRevenueBox}>
      <Box sx={titleRow}>
        <Box sx={leftSection}>
          <CoinsIcon width={24} height={24} />
          <Typography variant="h6" sx={titleText}>
            {isFuture ? 'Estimated monthly revenue' : 'Monthly revenue'}
          </Typography>

          <IconButton
            size="small"
            onClick={onDownloadClick}
            disabled={isDownloading || !selectedMonth}
            sx={isDownloading ? downloadButtonDisabled : downloadButton}
          >
            {isDownloading ? (
              <CircularProgress size={20} thickness={4} sx={circularProgress} />
            ) : (
              <CloudDownloadOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
        <Box sx={headerButtons}>
          <IconButton size="small" onClick={onClose}>
            <CollapaseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={mainValueRow}>
        <Typography variant="h3" sx={revenueAmount}>
          £{revenue.toLocaleString()}
        </Typography>
        <Box sx={monthSelectorContainer}>
          <ExpandedMonthlyRevenueMonthSelector
            months={months}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            isFuture={isFuture}
          />
        </Box>
      </Box>

      <Box sx={statsAndArrowsRow}>
        <ExpandedMonthlyRevenueStats
          bookingRate={bookingRate}
          estimatedRevenue={estimatedRevenue}
          isFuture={isFuture}
        />
        <Box sx={arrowNavContainer}>
          <ArrowMonthNavigation
            onPrev={handlePrev}
            onNext={handleNext}
            disablePrev={windowStart === 0}
            disableNext={windowStart + windowSize >= (monthlyData?.length || 0)}
          />
        </Box>
      </Box>
      {chartData.length > 0 && yAxisTicks.length > 0 && (
        <ExpandedMonthlyRevenueChart
          chartData={chartData}
          monthlyData={monthlyData}
          yAxisTicks={yAxisTicks}
        />
      )}
    </Box>
  );
};

export default ExpandedMonthlyRevenue;
